/**
 * ZONE SETTINGS (FILO-29) — Sezione «Zone e assegnazioni» delle Impostazioni:
 * le zone commerciali del sito con colore, commerciale e province. Una
 * provincia senza colore o commerciale propri usa quelli della zona.
 * Lettura per tutti, scrittura solo per il CRM Administrator (l'enforce reale
 * resta lato backend). Dopo ogni scrittura l'elenco si ricarica dal server,
 * perché `customerCount` lo calcola lui.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { zoneApi } from '../../entities/zone/zoneApi';
import { extractError } from '../../store/thunkHelpers';
import type { Zone, ZoneRegion } from '../../entities/zone/zoneTypes';
import { EditGlyph, PlusGlyph, TrashGlyph } from '../icons';
import { useSlotContributors } from '../../plugin/useSlotContributors';
import { useToast } from '../useToast';
import { ZoneDeleteDialog } from './ZoneDeleteDialog';
import { ZoneDialog } from './ZoneDialog';
import { ZoneRegionDialog } from './ZoneRegionDialog';

export function ZoneSettings() {
  // I blocchi dei plugin in fondo alla pagina: tutti i colori della mappa in un posto (F2: il colore
  // proprio dei commerciali).
  const extraBlocks = useSlotContributors('Settings', 'settings:zones');
  const { t } = useTranslation();
  const toast = useToast();
  const groupId = useCurrentGroupId();
  const isAdmin = useAppSelector((state) => state.auth.user?.administrator) ?? false;
  const [zones, setZones] = useState<Zone[] | null>(null);
  // `true` = dialogo di creazione; una zona = modifica.
  const [editing, setEditing] = useState<Zone | true | null>(null);
  const [deleting, setDeleting] = useState<Zone | null>(null);
  // Provincia della zona: senza `region` = aggiunta.
  const [editingRegion, setEditingRegion] = useState<{ zone: Zone; region?: ZoneRegion } | null>(null);

  const reload = useCallback(() => {
    zoneApi.list(groupId).then(setZones).catch((err) => {
      setZones([]);
      toast({ severity: 'error', message: extractError(err) });
    });
  }, [groupId, toast]);

  useEffect(() => { reload(); }, [reload]);

  function afterWrite() {
    setEditing(null);
    setDeleting(null);
    setEditingRegion(null);
    reload();
  }

  return (
    <div className="zone-settings">
      <div className="page-head">
        <h1 className="page-title">{t('settings.zones.title')}</h1>
      </div>

      <div className="zone-settings__bar">
        <div>
          <h2>{t('settings.zones.section')}</h2>
          <p className="text-muted">{t('settings.zones.hint')}</p>
        </div>
        {isAdmin && (
          <button type="button" className="btn-primary" disabled={zones === null} onClick={() => setEditing(true)}>
            <PlusGlyph /> {t('settings.zones.new')}
          </button>
        )}
      </div>

      {zones === null && <p className="text-muted">{t('common.loading')}</p>}
      {zones?.length === 0 && <p className="text-muted">{t('settings.zones.empty')}</p>}
      {zones && zones.length > 0 && (
        <div className="ltable-wrap">
          <div className="ltable__head">
            <span>{t('settings.zones.columns.color')}</span>
            <span>{t('settings.zones.columns.zone')}</span>
            <span>{t('settings.zones.columns.salesperson')}</span>
            <span>{t('settings.zones.columns.customers')}</span>
            <span />
          </div>
          {zones.map((z) => (
            <div key={z.zoneId} className="lrow">
              <span className="zone-settings__color-cell">
                <span className="zone-settings__swatch" style={{ background: z.color }} />
                <span className="cell-mono">{z.color}</span>
              </span>
              <span className="zone-settings__zone">
                <span>{z.name}</span>
                <span className="zone-settings__regions">
                  {z.regions.map((r) => {
                    const label = (
                      <>
                        <span className="zone-settings__dot" style={{ background: r.color || z.color }} />
                        {r.regionName} · {r.regionCode}
                        {r.liferayUserId > 0 && <span className="text-muted"> · {r.liferayUserName || `#${r.liferayUserId}`}</span>}
                      </>
                    );
                    return isAdmin ? (
                      <button key={r.zoneRegionId} type="button" className="zone-settings__region" onClick={() => setEditingRegion({ zone: z, region: r })}>
                        {label}
                      </button>
                    ) : (
                      <span key={r.zoneRegionId} className="zone-settings__region">{label}</span>
                    );
                  })}
                  {isAdmin && (
                    <button type="button" className="zone-settings__region zone-settings__region--add" onClick={() => setEditingRegion({ zone: z })}>
                      <PlusGlyph /> {t('settings.zones.regions.addShort')}
                    </button>
                  )}
                  {!isAdmin && z.regions.length === 0 && <span className="text-muted">{t('settings.zones.regions.none')}</span>}
                </span>
              </span>
              {/* Il backend manda null o stringa vuota se l'utente non c'è più: «—» per entrambi. */}
              <span>{z.liferayUserName || '—'}</span>
              <span>{z.customerCount}</span>
              <span className="lc-actions">
                {isAdmin && (
                  <>
                    <button type="button" className="lc-act" aria-label={t('settings.zones.editZone')} onClick={() => setEditing(z)}>
                      <EditGlyph />
                    </button>
                    <button type="button" className="lc-act del" aria-label={t('settings.zones.delete')} onClick={() => setDeleting(z)}>
                      <TrashGlyph />
                    </button>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ZoneDialog
          groupId={groupId}
          zone={editing === true ? undefined : editing}
          zones={zones ?? []}
          onClose={() => setEditing(null)}
          onSaved={afterWrite}
        />
      )}
      {editingRegion && (
        <ZoneRegionDialog
          zone={editingRegion.zone}
          region={editingRegion.region}
          zones={zones ?? []}
          onClose={() => setEditingRegion(null)}
          onSaved={afterWrite}
        />
      )}
      {deleting && <ZoneDeleteDialog zone={deleting} onClose={() => setDeleting(null)} onDeleted={afterWrite} />}

      {extraBlocks.map((c) => (
        <c.component key={c.key} entityId={0} />
      ))}
    </div>
  );
}
